import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Settings2, Clock, Users, ShieldAlert, Inbox, AlertOctagon } from 'lucide-react';
import { toast } from 'sonner';

// Lead routing is hardcoded to Listing Owner + Agency CC + 24h escalation.
// Other modes (round-robin, first-to-claim, manual) caused accountability
// confusion in production and have been removed from the UI per Zameen's
// industry-standard model. The backend still accepts the column, but the
// agency admin can only configure the SLA window now.

export function AgencySettings() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const mode = 'listing_owner'; // Hardcoded — UI no longer exposes mode selection.
  const [sla, setSla] = useState(30);

  const { data: agency } = useQuery({
    queryKey: ['agency-settings-agency', user?.id],
    queryFn: async () => {
      const { data } = await supabase.from('agencies').select('id, name').eq('created_by', user!.id).maybeSingle();
      return data;
    },
    enabled: !!user,
  });

  const { data: settings, isLoading } = useQuery({
    queryKey: ['agency-settings-row', agency?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('agency_settings')
        .select('*')
        .eq('agency_id', agency!.id)
        .maybeSingle();
      return data;
    },
    enabled: !!agency,
  });

  useEffect(() => {
    if (settings) {
      setSla(settings.sla_response_minutes);
    }
  }, [settings]);

  const save = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('agency_settings')
        .upsert({
          agency_id: agency!.id,
          lead_assignment_mode: mode,
          sla_response_minutes: Math.max(1, Math.min(1440, sla)),
        }, { onConflict: 'agency_id' });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agency-settings'] });
      qc.invalidateQueries({ queryKey: ['agency-settings-row'] });
      toast.success('Routing settings saved');
    },
    onError: (e: any) => toast.error(e.message || 'Failed to save'),
  });

  if (!agency) {
    return (
      <Card className="rounded-2xl">
        <CardContent className="p-8 text-center">
          <ShieldAlert className="h-10 w-10 mx-auto text-muted-foreground/50 mb-2" />
          <p className="text-sm text-muted-foreground">Only agency admins can configure routing settings.</p>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) return <Skeleton className="h-64 rounded-2xl" />;

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h2 className="font-display text-xl font-bold flex items-center gap-2">
          <Settings2 className="h-5 w-5 text-primary" /> Lead Routing Settings
        </h2>
        <p className="text-sm text-muted-foreground">Control how new inquiries are distributed across agents in <strong>{agency.name}</strong>.</p>
      </div>

      {/* Always-CC + 24h escalation callout */}
      <Card className="rounded-2xl border-0 bg-primary/5">
        <CardContent className="p-4 space-y-2">
          <div className="flex items-start gap-2">
            <Inbox className="h-4 w-4 text-primary mt-0.5 shrink-0" />
            <p className="text-sm">
              <strong>Agency admins always receive a copy</strong> of every inquiry, regardless of routing mode, and can reassign at any time from the
              <strong> Agency Lead Inbox</strong>.
            </p>
          </div>
          <div className="flex items-start gap-2">
            <AlertOctagon className="h-4 w-4 text-foreground mt-0.5 shrink-0" />
            <p className="text-sm">
              Leads not contacted within <strong>24 hours</strong> are automatically flagged as <em>Awaiting Reassignment</em> in the inbox. This 24h escalation is industry standard and not configurable.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-0 shadow-sm">
        <CardContent className="p-5 space-y-5">
          <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold mb-1">
              <Users className="h-4 w-4 text-primary" /> Assignment Mode: Listing Owner + Agency CC
            </div>
            <p className="text-xs text-muted-foreground">
              Every lead is routed to the agent who uploaded the listing. You (the agency admin)
              receive a copy at the same time and can reassign at any moment from the Agency
              Lead Inbox. This is industry standard and not configurable.
            </p>
          </div>

          <div>
            <Label className="text-sm font-semibold flex items-center gap-2 mb-2">
              <Clock className="h-4 w-4" /> Soft SLA — Response Window (minutes)
            </Label>
            <Input
              type="number" min={1} max={1440}
              value={sla}
              onChange={(e) => setSla(parseInt(e.target.value || '30'))}
              className="max-w-[160px]"
            />
            <p className="text-xs text-muted-foreground mt-1">Leads not contacted within this window are flagged 🔴 in the agent's inbox for internal urgency. The hard 24h escalation above is separate and always on.</p>
          </div>

          <div className="flex justify-end">
            <Button onClick={() => save.mutate()} disabled={save.isPending}>Save Settings</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
